import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { filter } from 'lodash';

class APIService {
  private instance: AxiosInstance;
  private userId: string = 'default_user';

  constructor() {
    const baseUrl = window.location.origin + '/editor';
    this.instance = axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this._setupInterceptors();
  }

  private _setupInterceptors(): void {
    // 请求拦截器, 使用cookie，不需要再手动设置token
    // this.instance.interceptors.request.use(
    //   (config: AxiosRequestConfig) => {
    //     const token = localStorage.getItem('auth_token') || '4MulDUmJ1DBtUKGXDF87PhVflulURc8F';
    //     if (token && config.headers) {
    //       config.headers.Authorization = `Bearer ${token}`;
    //     }
        
    //     config.headers = {
    //       ...config.headers,
    //       'X-User-ID': this.userId,
    //       'X-Request-ID': this._generateRequestId(),
    //     };

    //     // console.log('API Request:', config.method?.toUpperCase(), config.url);
    //     return config;
    //   },
    //   (error) => {
    //     console.error('请求拦截器错误:', error);
    //     return Promise.reject(error);
    //   }
    // );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // console.log('API Response:', response.status, response.config.url);
        return response.data;
      },
      (error) => {
        console.error('响应拦截器错误:', error);
        const loginRoute = (window as any).AGIMX_CONFIG.lOGIN_ROUTE;
        if (error.response?.status === 401) {
          // localStorage.removeItem('auth_token');
          window.location.href = loginRoute;
        } else if (error.response?.status >= 500) {
          console.error('服务器错误:', error.response.data);
        }
        
        return Promise.reject(error);
      }
    );
  }

  private _generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }


  // ========= 数据表格相关接口 =========

  /**
   * 获取表格列表
   * @returns 表格列表
   */
  async getCollectionList(): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.get('/collections');
      return response;
    } catch (error) {
      console.error('获取表格列表失败:', error);
      throw this._handleError(error, '获取表格列表失败');
    }
  }

  /**
   * 获取表格字段列表
   * @param collection 集合名称
   * @returns 表格字段列表
   */
  async getTableFields(collection: string): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.get(`/fields/${collection}`);
      return response;
    } catch (error) {
      console.error('获取表格列表失败:', error);
      throw this._handleError(error, '获取表格列表失败');
    }
  }

  /**
   * 获取表格数据
   * @param collection 集合名称
   * @returns 表格原始数据
   */
  async getTableData(collection: string, fields: string[]): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.get(`/items/${collection}`, {
        params: {
          fields: fields,
          sort: ['sort'], 
          limit: -1
        },
      });
      return response;
    } catch (error) {
      console.error('获取表格数据失败:', error);
      throw this._handleError(error, '获取表格数据失败');
    }
  }

  /**
   * 更新单个数据项
   * @param collection 集合名称
   * @param id 数据项ID
   * @param updateData 更新的数据，格式：{'columnField': 'newValue'}
   * @returns 更新后的数据
   */
  async updateTableItem(collection: string, id: string, updateData: Record<string, any>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.patch(`/items/${collection}/${id}`, updateData);
      return response;
    } catch (error) {
      console.error('更新表格数据失败:', error);
      throw this._handleError(error, '更新数据失败');
    }
  }

  /**
   * 批量更新数据项
   * @param collection 集合名称
   * @param updates 批量更新数据，格式：[{id: 'xxx', data: {'field': 'value'}}]
   * @returns 更新结果
   */
  async batchUpdateTableItems(collection: string, updates: Array<{id: string, data: Record<string, any>}>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.patch(`/items/${collection}`, updates);
      return response;
    } catch (error) {
      console.error('批量更新表格数据失败:', error);
      throw this._handleError(error, '批量更新数据失败');
    }
  }

  /**
   * 创建新的数据项
   * @param collection 集合名称
   * @param data 新增的数据
   * @returns 创建结果
   */
  async createTableItem(collection: string, data: Record<string, any>): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.post(`/items/${collection}`, data);
      return response;
    } catch (error) {
      console.error('创建表格数据失败:', error);
      throw this._handleError(error, '创建数据失败');
    }
  }

  /**
   * 删除数据项
   * @param collection 集合名称
   * @param id 数据项ID
   * @returns 删除结果
   */
  async deleteTableItem(collection: string, id: string): Promise<AxiosResponse<any>> {
    try {
      const response = await this.instance.delete(`/items/${collection}/${id}`);
      return response;
    } catch (error) {
      console.error('删除表格数据失败:', error);
      throw this._handleError(error, '删除数据失败');
    }
  }

  // ========= 仪表盘相关接口 =========

  /**
   * 创建仪表盘
   * @param data 
   * @returns 
   */
  public async createDashboard<T>(data: any): Promise<AxiosResponse<T>> {
    return this.instance.post<T>('/items/dashboard', data);
  }

  /**
   * 获取仪表盘列表
   * @param userId 用户ID
   * @returns 仪表盘列表
   */
  public async getDashboardList<T>(): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(`/items/dashboard`);
  }

  /**
   * 删除仪表盘
   * @param dashboardId 仪表盘ID
   * @returns 删除结果
   */
  public async deletetDashboard<T>(dashboardId: string): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(`/items/dashboard/${dashboardId}`);
  }

  /**
   * 获取仪表盘图表
   * @param dashboardId 仪表盘ID
   * @returns 仪表盘图表
   */
  public async getDashboardCharts<T>(dashboardId: number): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(`/items/dashboard_charts`, {
      params: {
        filter: {
          parentId: {
            _eq: dashboardId,
          },
        },
      },
    });
  }

  /**
   * 创建仪表盘图表
   * @param data 图表数据
   * @returns 创建结果
   */
  public async createDashboardChart<T>(data: any): Promise<AxiosResponse<T>> {
    return this.instance.post<T>('/items/dashboard_charts', data);
  }

  /**
   * 更新仪表盘图表
   * @param chartId 图表ID
   * @param data 图表数据
   * @returns 更新结果
   */
  public async updateDashboardChart<T>(chartId: number, data: any): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(`/items/dashboard_charts/${chartId}`, data);
  }

  /**
   * 删除仪表盘图表
   * @param chartId 图表ID
   * @returns 删除结果
   */
  public async deleteDashboardChart<T>(chartId: number): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(`/items/dashboard_charts/${chartId}`);
  }

  // ========= 通用方法 =========

  private _handleError(error: any, defaultMessage: string): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error(defaultMessage);
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  getUserId(): string {
    return this.userId;
  }

  // ========= 通用 HTTP 方法 =========

  public async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data);
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  public async put<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data);
  }

  public async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url);
  }
}

export default new APIService();